import React, { useState } from "react";
import { Box, IconButton, Toolbar, Tooltip } from "@mui/material";
import { Edit, Lock, Person, PersonAddAlt, PersonOff } from "@mui/icons-material";
import { fetchUsers, updateUser } from "../../scripts/api_v2/user_client";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "../../hooks/use_backend";
import { isComponentDisabledForUpdates } from "../login/login_utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import ReactTableWithSettings from "../../components/material_react_table/react_table_with_settings";
import { REACT_TABLE_NAMES } from "../../enums/react_table_name_enum";
import Snackbar from "../../scripts/snackbar";
import UserPopover from "./add_user_popover";
import { UserActions } from "./user_actions_enum";
import { fetchPartnerCompanies } from "../../scripts/api_v2/partner_company_client";
import { fetchUserRoles } from "../../scripts/api_v2/role_client";
import { BrusselsDateTimeStringToDate } from "../../scripts/utils";

export default function UserManagementTable({ required_privilege }) {
  const backend = useBackend();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const isDisabled = isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());
  const [popoverData, setPopoverData] = useState({
    open: false,
    anchorEl: null,
    userRow: {},
    actionType: "",
    partnerCompaniesData: null,
    companiesError: false,
    userRolesData: null,
    rolesError: false,
  });

  const handlePopoverOpen = (event, row, type) => {
    if (popoverData.open === false) {
      setPopoverData({
        ...popoverData,
        open: true,
        anchorEl: event.currentTarget,
        userRow: row,
        actionType: type,
        partnerCompaniesData: partnerCompaniesData,
        companiesError: companiesError,
        userRolesData: userRolesData,
        rolesError: rolesError,
      });
    }
  };

  const handlePopoverClose = () => {
    if (popoverData.open === true) {
      setPopoverData({
        ...popoverData,
        open: false,
        anchorEl: null,
        userRow: {},
        actionType: "",
        partnerCompaniesData: null,
        companiesError: false,
        userRolesData: null,
        rolesError: false,
      });
      refetch();
    }
  };

  const {
    data: userData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.USER_OVERVIEW, backend.fetch],
    queryFn: () => fetchUsers(backend.fetch),
  });

  const { data: partnerCompaniesData, error: companiesError } = useQuery({
    queryKey: [REACT_QUERY_KEYS.AVAILABLE_PARTNER_COMPANIES, backend.fetch],
    queryFn: () => fetchPartnerCompanies(backend.fetch),
  });

  const { data: userRolesData, error: rolesError } = useQuery({
    queryKey: [REACT_QUERY_KEYS.AVAILABLE_USER_ROLES, backend.fetch],
    queryFn: () => fetchUserRoles(backend.fetch),
  });

  const { mutate: doUserMutation, isPending: userIsPending } = useMutation({
    mutationFn: async (data) => await updateUser(backend.fetch, data),
    onSuccess: () => {
      Snackbar.success("User updated");
      refetch();
    },
    onError: (err, data, context) => {
      Snackbar.error("User update failed: " + err);
    },
  });

  const formattedData = React.useMemo(() => {
    if (!userData) return [];
    else {
      const allKeys = Object.keys(userData[0]);
      const dateKeys = [];
      allKeys.forEach((key) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes("date")) {
          dateKeys.push(key);
        }
      });
      return userData.map((row) => {
        const sanitizedRow = { ...row };
        Object.keys(sanitizedRow).forEach((key) => {
          if (key.toLowerCase().includes("roles")) {
            sanitizedRow["role_name"] = parse_roles(row[key]);
          }
          if (key.toLowerCase().includes("company")) {
            sanitizedRow["company_name"] = row[key].company_name;
          }
        });
        dateKeys.forEach((key) => {
          sanitizedRow[key] = BrusselsDateTimeStringToDate(sanitizedRow[key]);
        });
        return sanitizedRow;
      });
    }
  }, [userData]);

  const defaultVisibility = {
    actions: true,
    user_id: true,
    username: true,
    role_name: true,
    company_name: true,
    name: true,
    active: true,
    signed_nda: true,
    signed_nda_date: true,
  };

  const defaultSorting = [{ id: "username", desc: false }];

  function toUnicodeCheck(value) {
    return value ? "\u2713" : ""; //"\u2715";
  }

  const columns = [
    {
      accessorKey: "actions",
      header: "ACTIONS",
      enableClickToCopy: false,
      enableGlobalFilter: false,
      Cell: ({ row }) => (
        <div style={{ alignItems: "center" }}>
          <Tooltip title="Edit User">
            <IconButton
              disabled={isDisabled}
              onClick={(event) => handlePopoverOpen(event, row.original, UserActions.EDIT_USER)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Change Password">
            <IconButton
              disabled={isDisabled}
              onClick={(event) => handlePopoverOpen(event, row.original, UserActions.RESET_USER_PASSWORD)}
            >
              <Lock />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.original.active ? "Deactivate User" : "Activate User"}>
            <IconButton
              disabled={isDisabled}
              onClick={() => doUserMutation({ ...row.original, action: UserActions.ACTIVATE_DEACTIVATE_USER.action })}
            >
              {row.original.active ? <PersonOff /> : <Person />}
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    { accessorKey: "user_id", header: "USER ID", enableClickToCopy: true },
    { accessorKey: "username", header: "USERNAME", enableClickToCopy: true },
    { accessorKey: "role_name", header: "ROLE NAME", enableClickToCopy: true },
    { accessorKey: "company_name", header: "COMPANY NAME", enableClickToCopy: true },
    { accessorKey: "name", header: "NAME", enableClickToCopy: true },
    {
      accessorKey: "signed_nda",
      header: "SIGNED NDA",
      enableClickToCopy: false,
      enableGlobalFilter: false,
      filterVariant: "checkbox",
      Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
    },
    {
      accessorKey: "signed_nda_date",
      header: "SIGNED NDA DATE",
      enableClickToCopy: true,
      enableGlobalFilter: false,
      filterVariant: "datetime-range",
      Cell: ({ cell }) => (cell.getValue() ? cell.getValue().toLocaleString() : null),
      sortingFn: "customDateSortingFn",
      filterFn: "customDatetimeRangeFilter",
    },
    {
      accessorKey: "active",
      header: "ACTIVE",
      enableClickToCopy: false,
      enableGlobalFilter: false,
      filterVariant: "checkbox",
      Cell: ({ cell }) => toUnicodeCheck(cell.getValue()),
    },
  ];

  function parse_roles(roles) {
    return roles
      ? roles.map((role) => role.application + ": " + role.role_name + parse_root_access(role)).join("\n")
      : "";
  }

  function parse_root_access(row) {
    if (row.root_access) {
      return " - root access";
    }
    return "";
  }

  function addUserButton() {
    return (
      <Toolbar sx={{ justifyContent: "center" }}>
        <IconButton color="primary" onClick={(event) => handlePopoverOpen(event, {}, UserActions.ADD_USER)}>
          <PersonAddAlt />
        </IconButton>
      </Toolbar>
    );
  }

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <ReactTableWithSettings
          unique_table_key={REACT_TABLE_NAMES.USER_MANAGEMENT}
          columns={columns}
          data={formattedData ? formattedData : []}
          error={error}
          isLoading={isLoading || userIsPending}
          defaultVisibility={defaultVisibility}
          defaultSorting={defaultSorting}
          topToolbarComponent={addUserButton()}
        />
      </Box>
      {popoverData.open ? (
        <UserPopover
          open={popoverData.open}
          anchorEl={popoverData.anchorEl}
          userRow={popoverData.userRow}
          actionType={popoverData.actionType}
          onClose={handlePopoverClose}
          partnerCompaniesPickList={popoverData.partnerCompaniesData}
          companiesError={popoverData.companiesError}
          userRolesPickList={popoverData.userRolesData}
          rolesError={popoverData.rolesError}
        />
      ) : null}
    </>
  );
}
